import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import {
  getEvolution,
  getNetworkOverview,
  getRegionDistribution,
  getRejectionReasons,
  getStates,
} from '../../../../../../api/theHive/redeProto';
import {
  EvolutionPoint,
  NetworkOverview,
  Pillar,
  RegionDistribution,
  StateSummary,
} from '../../../../../../api/theHive/redeProto/types';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { RedeProtoOutletContext } from '../../types';
import { composeBsc } from '../../utils/composeBsc';

interface UseOverviewHook {
  overview: NetworkOverview | undefined;
  pillars: Pillar[];
  states: StateSummary[];
  evolution: EvolutionPoint[];
  regionDistribution: RegionDistribution[];
  loading: boolean;
  error: boolean;
}

export default function useOverview(): UseOverviewHook {
  const [overview, setOverview] = useState<NetworkOverview>();
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [states, setStates] = useState<StateSummary[]>([]);
  const [evolution, setEvolution] = useState<EvolutionPoint[]>([]);
  const [regionDistribution, setRegionDistribution] = useState<
    RegionDistribution[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const { selectedNoticeId } = useOutletContext<RedeProtoOutletContext>();

  const getData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [overviewRes, reasonsRes, statesRes, evolutionRes, regionRes] =
        await Promise.all([
          getNetworkOverview(selectedNoticeId),
          getRejectionReasons(selectedNoticeId),
          getStates(selectedNoticeId),
          getEvolution(undefined, selectedNoticeId),
          getRegionDistribution(selectedNoticeId),
        ]);

      const responses = [
        overviewRes,
        reasonsRes,
        statesRes,
        evolutionRes,
        regionRes,
      ];
      responses.forEach((response) => {
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
        if (!response.data) {
          throw new Error('Algo deu errado, tente novamente.');
        }
      });

      const evolutionData = evolutionRes.data ?? [];
      setOverview(overviewRes.data);
      setPillars(
        composeBsc(overviewRes.data, evolutionData, reasonsRes.data ?? [])
      );
      setStates(statesRes.data ?? []);
      setEvolution(evolutionData);
      setRegionDistribution(regionRes.data ?? []);
    } catch (requestError) {
      setError(true);
      handleSnackbar(getErrorMessage(requestError), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNoticeId]);

  useEffect(() => {
    getData();
  }, [getData]);

  return {
    overview,
    pillars,
    states,
    evolution,
    regionDistribution,
    loading,
    error,
  };
}
